/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ProdutosService } from '../../../shared/services/api/estoque/produtos/ProdutosService';
import { CartaoFlutuanteCarregando } from '../../../shared/components';

export type TTipoAjusteEstoque = 'entrada' | 'saida';

interface IDialogAjusteEstoqueProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (novaQuantidade: number) => void;
  tipoAjuste: TTipoAjusteEstoque;
  idproduto: string;
  descricaoProduto: string;
  un: string;
  precoUnitario: number;
  quantidadeAjuste: number;
  quantidadeAtual: number;
}

export const DialogAjusteEstoque: React.FC<IDialogAjusteEstoqueProps> = ({
  open,
  onClose,
  onSuccess,
  tipoAjuste,
  idproduto,
  descricaoProduto,
  un,
  precoUnitario,
  quantidadeAjuste,
  quantidadeAtual,
}) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const quantidadeFinal = quantidadeAtual + (tipoAjuste === 'entrada' ? quantidadeAjuste : -quantidadeAjuste);

  // O componente permanece montado entre um ajuste e outro (só o <Dialog open> muda),
  // então o msg/openmsg do ajuste anterior sobrevive e pisca na abertura seguinte se
  // não for limpo aqui.
  useEffect(() => {
    if (open) {
      setOpenmsg(false);
      setMsg('');
      setIsLoading(false);
    }
  }, [open]);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const handleConfirm = () => {
    setIsLoading(true);

    ProdutosService.updateByIdReajuste(idproduto, quantidadeAjuste, tipoAjuste, un)
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setTms('success');
          setMsg(tipoAjuste === 'entrada' ? 'Entrada de estoque realizada com sucesso!' : 'Saída de estoque realizada com sucesso!');
          setOpenmsg(true);
          const novaQuantidade = quantidadeAtual + (tipoAjuste === 'entrada' ? quantidadeAjuste : -quantidadeAjuste);
          setTimeout(() => {
            onSuccess(novaQuantidade);
          }, 600);
        }
      });
  };

  const corDestaque = tipoAjuste === 'entrada' ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { position: 'relative' } }}>
      <CartaoFlutuanteCarregando
        open={isLoading}
        message={tipoAjuste === 'entrada' ? 'Fazendo entrada...' : 'Fazendo saída...'}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={tms === 'error' ? null : 2500}
        onClose={handleCloseMsg}
      >
        <Alert onClose={handleCloseMsg} severity={tms} sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>

      <DialogTitle>
        {tipoAjuste === 'entrada' ? 'Ajuste de estoque — Entrada' : 'Ajuste de estoque — Saída'}
      </DialogTitle>

      <DialogContent dividers>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(corDestaque, 0.08),
            border: `1px solid ${corDestaque}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>{descricaoProduto}</Typography>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">Quantidade atual</Typography>
              <Typography variant="body2">{quantidadeAtual} {un}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                {tipoAjuste === 'entrada' ? 'Quantidade a entrar' : 'Quantidade a sair'}
              </Typography>
              <Typography variant="body2" fontWeight={700}>{quantidadeAjuste} {un}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">Quantidade em estoque</Typography>
              <Typography variant="body2" fontWeight={700}>{quantidadeFinal} {un}</Typography>
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={isLoading}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={isLoading}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};
